import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import TechnologyList from '@/components/projects/technology-list'
import { SampleBadge } from '@/components/elements/sample-notice'

interface ProjectListItemProps {
    project: ProjectSummary
}

/** One row of the project index: year, title and summary, category, stack. */
const ProjectListItem: FC<ProjectListItemProps> = ({ project }): ReactNode => {
    return (
        <li className='group relative border-b border-border'>
            <div className='grid grid-cols-4 gap-x-4 gap-y-3 py-7 transition-colors duration-200 sm:gap-x-6 md:grid-cols-12 md:py-8 lg:gap-x-8'>
                <time dateTime={project.date} className='col-span-1 font-mono text-caption text-muted-foreground md:col-span-1 md:pt-1.5'>
                    {project.year}
                </time>

                <div className='col-span-3 flex flex-col gap-2 md:col-span-6'>
                    <h3 className='font-heading text-heading [font-weight:450]'>
                        <Link href={`/projects/${project.slug}`} className='transition-colors duration-200 after:absolute after:inset-0 group-hover:text-brand'>
                            {project.title}
                        </Link>
                    </h3>

                    <p className='max-w-[56ch] text-body text-muted-foreground'>
                        {project.description}
                    </p>
                </div>

                <div className='col-span-4 flex flex-wrap items-center gap-2 md:col-span-2 md:flex-col md:items-start md:pt-2'>
                    <span className='font-mono text-label uppercase text-muted-foreground'>{project.categoryLabel}</span>
                    {project.sample && <SampleBadge />}
                </div>

                <div className='col-span-4 flex items-start justify-between gap-4 md:col-span-3 md:pt-2'>
                    <TechnologyList items={project.technologies.slice(0, 4)} />

                    <ArrowRight
                        aria-hidden='true'
                        className='mt-0.5 size-4 shrink-0 text-muted-foreground transition-[transform,color] duration-300 ease-editorial group-hover:translate-x-1 group-hover:text-brand'
                    />
                </div>
            </div>
        </li>
    )
}

export default ProjectListItem
